import { CustomOption, Product } from '../../types/Product';
import { PriceObject } from '../../features/pdp/components/ProductDetails/ProductPrice';
import _ from 'lodash';
import { math } from 'helpers/math';
import { getCustomOptionDefaults, getProductOptionsFromParams, getProductPriceForOptions } from '../../features/pdp';

export enum VariesBy {
  COLOR = 'color',
  SIZE = 'size',
  MATERIAL = 'material',
  PATTERN = 'pattern'
}

export enum ItemAvailability {
  IN_STOCK = 'https://schema.org/InStock',
  OUT_OF_STOCK = 'https://schema.org/OutOfStock'
}

type VariesByMap<T = string> = {
  [key in VariesBy]?: T;
};

// TODO: Update w/ correct option mapping for all products
const CUSTOM_OPTIONS_MAP: VariesByMap<string[]> = {
  [VariesBy.SIZE]: [
    'album_size',
    'book_size',
    'box_size',
    'canvas_frame_size',
    'deep_frame_size',
    'float_frame_size',
    'frame_size',
    'gallery_frame_size',
    'hardcover_book_size',
    'matrefill_size',
    'print_size',
    'size',
    'softcover_book_size',
    'tabletop_frame_size',
    'tile_size',
    'wood_ledge_size',
    'wooden_box_size'
  ],
  [VariesBy.COLOR]: ['cover_color', 'envelope', 'fabric_color', 'frame_finish'],
  [VariesBy.MATERIAL]: ['frame_material'],
  [VariesBy.PATTERN]: []
};

export interface ProductVariant extends VariesByMap {
  options: ProductVariantOption[];
  price: PriceObject;
}

export interface ProductVariantOption {
  variesBy: VariesBy;
  value: string;
  displayValue: string;
  dependsOnOption: string;
  dependsOnValues: string;
  customOptionName: string;
}

export const getProductVariants = (product: Product): ProductVariant[] => {
  if (!product.customOptions || product.customOptions.length < 1) {
    return [];
  }

  const optionsByType = getOptionsByVariantType(product.customOptions);
  const variantOptions = Object.values(optionsByType).flatMap((o) => o);

  const filteredVariantOptions: ProductVariantOption[][] = product.customOptions
    .filter((option) => variantOptions.includes(option.name))
    .map((option) => {
      const variesBy = _.findKey(optionsByType, (value: string) => value === option.name) as VariesBy;
      return { ...option, variesBy };
    })
    .map((option) =>
      option.values.map((value) => ({
        variesBy: option.variesBy,
        value: value.label,
        displayValue: value.display_name || value.label,
        dependsOnOption: value.depends_on_option,
        dependsOnValues: value.depends_on_values,
        customOptionName: optionsByType[option.variesBy]
      }))
    );

  // We want all combinations of product options, so we get all subsets of product
  // options and generate all combinations of each of those option subsets
  const combinations: ProductVariantOption[][] = [];
  const optionSubsets = math.allSubsets(filteredVariantOptions);
  optionSubsets.forEach((subset) => {
    const subsetCombinations = math.cartesianProduct(...subset);
    combinations.push(...subsetCombinations);
  });

  return combinations.map((combination) => getProductVariantFromOptions(product, combination));
};

const getOptionsByVariantType = (customOptions: CustomOption[]): VariesByMap => {
  const resultMap: VariesByMap = {};
  Object.values(VariesBy).forEach((variant) => {
    for (const option of customOptions) {
      if (CUSTOM_OPTIONS_MAP[variant]?.includes(option.name)) {
        resultMap[variant] = option.name;
        return;
      }
    }
  });

  return resultMap;
};

const getProductVariantFromOptions = (product: Product, options: ProductVariantOption[]): ProductVariant => {
  const params = options.map((option) => option.value);
  const variantOptions = getProductOptionsFromParams(product, params);
  const defaultOptions = getCustomOptionDefaults(product);

  // We need to combine the specific options for the variant with the default options
  // of the product so that price calculations will be correct, specifically for
  // frames/prints where quantity/paper type are needed for price calculation
  const priceOptions = {
    ...defaultOptions,
    ...variantOptions
  };

  const price = getProductPriceForOptions(product, priceOptions);

  // We need to get the default variesBy values so that a variant for example
  // that only has a size specified when the product varies by size and color
  // will get a default color to supply to the markup
  const defaultVariesByValues = {};
  const optionsByVariantType = getOptionsByVariantType(product.customOptions);
  for (const [variesBy, optionName] of Object.entries(optionsByVariantType)) {
    const option = product.customOptions.find((option) => option.name === optionName);
    defaultVariesByValues[variesBy] = option?.values?.[0]?.label;
  }

  const variant: ProductVariant = {
    options,
    price,
    ...defaultVariesByValues
  };

  // Override variesBy attributes with specifics for this variant based on its options
  options.forEach((option) => {
    variant[option.variesBy] = option.displayValue;
  });
  return variant;
};

/**
 * Returns all option strings for a given product variant, which can be used to build the URL to deeplink to the
 * variant, as well as to generate a unique name for the variant.
 *
 * This also adds any option values that the variant's options may depend on, e.g. a rose Layflat depends on
 * the fabric collection being "limited" instead of "standard", so "limited" will be returned in the option strings
 *
 * @param variant the ProductVariant in question
 * @param customOptions the custom options associated with the base product
 */
export const getOptionsStringsForProductVariant = (
  variant: ProductVariant,
  customOptions: CustomOption[]
): { [x: string]: string } => {
  const optionStringsMap = {};
  variant.options.forEach(({ value, dependsOnOption, dependsOnValues, customOptionName }) => {
    optionStringsMap[customOptionName] = value;

    //Dependent option
    const requiredOption = customOptions?.find((option) => option.name === dependsOnOption);
    const requiredValue = dependsOnValues?.split(',')?.[0];
    const requiredValueIsDefault = requiredOption?.values?.[0].label === requiredValue;

    // Only add if option is dependent on a value that is not already the default
    if (requiredValue && !requiredValueIsDefault) {
      optionStringsMap[requiredOption?.name ?? requiredValue] = requiredValue;
    }
  });

  return optionStringsMap;
};
